import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput, LabeledStatus } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiMusicNotesPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetMusicProductsPhysicalList } from 'services/music-product-physical';
import { RELEASE_TYPE } from 'services/music-product-physical/types/enums';
import {
  IMusicProductPhysicalResponse,
  IMusicProductPhysicalSearch,
} from 'services/music-product-physical/types/query';
import { STATUS } from 'services/types';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { formatDate } from 'utils/global/date.utils';
import { getNodes } from 'utils/table/nodes.utils';

enum MUSIC_PRODUCT_PHYSICAL_COLUMNS {
  title,
  release,
  ean,
  copyrights,
  releaseStatus,
  // externalIds,
}

const musicProductPhysicalSearchInitialValues: IMusicProductPhysicalSearch = {
  ean: '',
  artistName: '',
  artistNickname: '',
  isrc: '',
  title: '',
  status: undefined,
};

const MusicProductsPhysical = () => {
  usePermissionGuard('PhysicalMusicProductPermissionsLIST**', '/', true, 'music-product-digital');

  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasPermission = useHasPermission();

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const columnsVisibility = useTableColumnsVisibility(
    'MusicProductsPhysical2',
    MUSIC_PRODUCT_PHYSICAL_COLUMNS
  );

  const i18nextPrefix = 'music-product-physical.list';

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters =
    searchById[columnsVisibility.tableId] ||
    musicProductPhysicalSearchInitialValues;

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetMusicProductsPhysicalList(filters);

  const tableData = getNodes<IMusicProductPhysicalResponse>(data?.pages.flat());

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/contracts`, {
      relative: 'path',
    });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const hasDetailPermission = hasPermission('PhysicalMusicProductPermissionsLIST**', 16);
  const tableSelect = hasDetailPermission ? select : undefined;

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={MUSIC_PRODUCT_PHYSICAL_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix={i18nextPrefix}
                />
              </div>
              {hasPermission('PhysicalMusicProductPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={() => navigate('create/tracks', { replace: false })}>
                  <Button.Text
                    text={t('button.create-what', {
                      what: t(`${i18nextPrefix}.what`),
                    })}
                  />
                  <Button.Icon icon={<PiMusicNotesPlus />} />
                </Button>
              )}
            </>
          }>
          <QueryFilter<IMusicProductPhysicalSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IMusicProductPhysicalSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(columnsVisibility.tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text name='title' label={t('input.label.title')} />
                  <FormInput.Text
                    name='artistNickname'
                    label={t('input.label.artistNickname')}
                  />
                  <FormInput.Text
                    name='artistName'
                    label={t('input.label.artistName')}
                  />
                  <FormInput.Text
                    name='ean'
                    label={t('input.label.ean')}
                    placeholder='0000000000'
                  />
                  <FormInput.Mask
                    name='isrc'
                    label='ISRC'
                    mask='AA-___-##-#####'
                    placeholder='US-P03-19-00001'
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        columns={[
          {
            label: `${t(`${i18nextPrefix}.table.title`)}`,
            renderCell: ({ productImagePreviewLink, mainTitle, id }) => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image
                    url={productImagePreviewLink}
                    alt={mainTitle.title}
                  />

                  <div className='flex flex-col'>
                    <TableItem.Text text={mainTitle.title} />
                    {mainTitle?.version && (
                      <TableItem.Text
                        text={mainTitle?.version}
                        isSupplementary
                      />
                    )}
                  </div>
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_PHYSICAL_COLUMNS.title),
          },
          {
            label: `${t(`${i18nextPrefix}.table.release`)}`,
            renderCell: ({ releaseType, releaseDate }) => (
              <TableItem>
                <TableItem.Text
                  text={
                    releaseType
                      ? t(`enum.RELEASE_TYPE.${RELEASE_TYPE[releaseType]}`)
                      : undefined
                  }
                />
                <TableItem.Text
                  text={releaseDate ? formatDate(releaseDate) : undefined}
                  isSupplementary
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_PHYSICAL_COLUMNS.release
            ),
          },
          {
            label: `${t(`${i18nextPrefix}.table.ean`)}`,
            renderCell: ({ ean }) => (
              <TableItem>
                <TableItem.Text text={ean ?? undefined} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_PHYSICAL_COLUMNS.ean),
          },
          {
            label: t(`${i18nextPrefix}.table.copyrights`),
            renderCell: ({ cLine, pLine }) => (
              <TableItem>
                <TableItem.Text
                  isSupplementary
                  text={
                    cLine?.name && cLine?.year
                      ? `© ${cLine.year} ${cLine.name}`
                      : undefined
                  }
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    pLine?.name && pLine?.year
                      ? `℗ ${pLine.year} ${pLine.name}`
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_PHYSICAL_COLUMNS.copyrights
            ),
          },
          {
            label: t(`${i18nextPrefix}.table.releaseStatus`),
            renderCell: ({ releaseStatus }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={releaseStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_PHYSICAL_COLUMNS.releaseStatus
            ),
          },
        ]}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        data={tableData}
        displayMode
      />
    </TopbarPage>
  );
};

export default MusicProductsPhysical;